import { usePacksSimple } from '@modules/Packs';
import { Combobox } from '@shared/components/ui/AdvancedFilter/components';
import { ProductCombobox } from '@shared/components/ui';
import { Button } from '@shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shared/components/ui/shadcn/dialog';
import { Label } from '@shared/components/ui/shadcn/label';
import { RadioGroup, RadioGroupItem } from '@shared/components/ui/shadcn/radio-group';
import { useToast } from '@shared/hooks/use-toast';
import { useProductSearch } from '@shared/hooks/useProductSearch';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePatchAdSpendCampaign } from '../../hooks';
import type { ProfitabilityAdCampaign } from '../../models/Profitability';
import { getProfitabilityMutationErrorMessage } from '../../utils/profitabilityApiError';

type LinkMode = 'product' | 'pack' | 'none';

interface LinkAdCampaignModalProps {
  campaign: ProfitabilityAdCampaign | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const LinkAdCampaignModal = ({ campaign, open, onOpenChange }: LinkAdCampaignModalProps) => {
  const { t } = useTranslation('profitabilityModule');
  const { toast } = useToast();
  const { searchProductsForCombobox } = useProductSearch();
  const { packs, isLoading: packsLoading } = usePacksSimple({ enabled: open });
  const patchM = usePatchAdSpendCampaign();

  const [mode, setMode] = useState<LinkMode>('none');
  const [productId, setProductId] = useState('');
  const [packId, setPackId] = useState('');

  const packOptions = useMemo(
    () =>
      packs.map((p) => ({
        label: p.title ?? `#${p.id}`,
        value: String(p.id),
      })),
    [packs]
  );

  const resetFromCampaign = useCallback((c: ProfitabilityAdCampaign | null) => {
    if (!c) {
      setMode('none');
      setProductId('');
      setPackId('');
      return;
    }
    if (c.product_id != null) {
      setMode('product');
      setProductId(String(c.product_id));
      setPackId('');
    } else if (c.pack_id != null) {
      setMode('pack');
      setPackId(String(c.pack_id));
      setProductId('');
    } else {
      setMode('none');
      setProductId('');
      setPackId('');
    }
  }, []);

  useEffect(() => {
    if (open && campaign) {
      resetFromCampaign(campaign);
    }
  }, [open, campaign, resetFromCampaign]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!campaign) return;

    let body: { product_id: number | null; pack_id: number | null };
    if (mode === 'product') {
      const pid = Number(productId);
      if (!Number.isFinite(pid) || pid < 1) {
        toast({
          title: t('ad_spend.toast.validation'),
          description: t('ad_spend.validation_product'),
          variant: 'destructive',
        });
        return;
      }
      body = { product_id: pid, pack_id: null };
    } else if (mode === 'pack') {
      const pk = Number(packId);
      if (!Number.isFinite(pk) || pk < 1) {
        toast({
          title: t('ad_spend.toast.validation'),
          description: t('ad_spend.validation_pack'),
          variant: 'destructive',
        });
        return;
      }
      body = { product_id: null, pack_id: pk };
    } else {
      body = { product_id: null, pack_id: null };
    }

    try {
      await patchM.mutateAsync({ id: campaign.id, body });
      toast({ title: t('ad_spend.toast.link_saved') });
      onOpenChange(false);
    } catch (err) {
      toast({
        title: t('ad_spend.toast.error'),
        description: getProfitabilityMutationErrorMessage(err, t('errors.generic')),
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg border-border bg-background" data-testid="profitability-ad-campaign-link-modal">
        <DialogHeader>
          <DialogTitle>{t('ad_spend.link_modal_title')}</DialogTitle>
        </DialogHeader>
        {campaign && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{campaign.name || campaign.external_id}</span>
              {campaign.external_id ? (
                <span className="ml-2 tabular-nums text-muted-foreground">({campaign.external_id})</span>
              ) : null}
            </p>

            <div className="space-y-3">
              <Label className="text-foreground">{t('ad_spend.link_mode_label')}</Label>
              <RadioGroup
                value={mode}
                onValueChange={(v) => setMode(v as LinkMode)}
                className="flex flex-col gap-3"
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="none" id="ad-link-none" />
                  <Label htmlFor="ad-link-none" className="cursor-pointer font-normal text-foreground">
                    {t('ad_spend.link_mode_none')}
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="product" id="ad-link-product" />
                  <Label htmlFor="ad-link-product" className="cursor-pointer font-normal text-foreground">
                    {t('ad_spend.link_mode_product')}
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="pack" id="ad-link-pack" />
                  <Label htmlFor="ad-link-pack" className="cursor-pointer font-normal text-foreground">
                    {t('ad_spend.link_mode_pack')}
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {mode === 'product' && (
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">{t('ad_spend.field_product')}</Label>
                <ProductCombobox
                  value={productId}
                  setValue={setProductId}
                  placeholder={t('ad_spend.field_product_placeholder')}
                  searchFn={searchProductsForCombobox}
                  dataTestId="profitability-ad-campaign-product"
                />
              </div>
            )}

            {mode === 'pack' && (
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">{t('ad_spend.field_pack')}</Label>
                <Combobox
                  options={packOptions}
                  value={packId}
                  setValue={setPackId}
                  placeholder={t('ad_spend.field_pack_placeholder')}
                  disabled={packsLoading}
                  dataTestId="profitability-ad-campaign-pack"
                />
              </div>
            )}

            <DialogFooter className="gap-2 sm:justify-end">
              <Button type="button" variant="outline" className="border-border" onClick={() => onOpenChange(false)}>
                {t('ad_spend.cancel')}
              </Button>
              <Button type="submit" disabled={patchM.isPending} data-testid="profitability-ad-campaign-link-save">
                {t('ad_spend.save_link')}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
